import { Color, Spacing } from '@persona-id/design-tokens';
import { MoreIcon, TransferOutIcon } from '@persona-id/design-tokens/icons';
import { BORDER_RADIUSES } from 'shared/styles/variables';
import styled from 'styled-components';

import AttributeItem, { AttributeGrid } from 'dashboard/components/AttributeItem';
import ModalWrapper from 'dashboard/components/ModalWrapper';
import AsyncImage from 'dashboard/components-lib/AsyncImage';
import ConfirmationModal from 'dashboard/components-lib/ConfirmationModal';
import FileUpload from 'dashboard/components-lib/FileUpload';
import { FileWithPreview } from 'dashboard/components-lib/FileUpload/types';
import { MessageType } from 'dashboard/lib/interfaces/Message';
import { formatNumberLarge } from 'dashboard/lib/utils/formatters';
import { Case, CaseAttachment } from 'dashboard/models/case';
import Button, { IconButton } from 'design-system/components/Button';
import Card from 'design-system/components/Card';
import DropdownMenu from 'design-system/components/DropdownMenu/DropdownMenu';
import Spacer from 'design-system/components/Spacer';
import { Heading, Paragraph } from 'design-system/components/Text';
import theme from 'design-system/theme';
import download from 'lib/download';
import { createAttachment, readFile } from 'lib/file';
import useConfirmation from 'lib/hooks/useConfirmation';
import { captureMessage } from 'lib/reporting';
import { PreparedFileUpload } from 'lib/utils/dataUrl';

const ACCEPTED_FILE_FORMATS = [
  'application/pdf',
  'image/jpeg',
  'image/png',
  'image/heic',
  'image/heif',
  'application/zip',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // xlsx
  'application/vnd.ms-excel', // xls
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // docx
  'text/csv',
  'text/plain', // txt
  'text/tab-separated-values', // tsv
];
const MAX_ATTACHMENT_SIZE_BYTES = 25000000; // 25MB
const MAX_FILE_COUNT = 10;

const InfoSection = styled.div`
  display: flex;
  flex-direction: column;
  position: relative;
`;

const AttachmentContainer = styled.div`
  display: grid;
  grid-template-columns: max-content auto;
  grid-column-gap: ${Spacing[5]};
  padding: ${Spacing[5]};
  border: 1px solid ${theme.color.border.primary};
  border-radius: ${BORDER_RADIUSES.rounded};
  margin: ${Spacing[2]} 0px;
`;

const IconUploadCentered = styled(TransferOutIcon)`
  height: 24px;
  width: 24px;
  align-self: center;
  margin-bottom: ${Spacing[2]};
  color: ${Color.CorePurple800};
`;

const FileUploadButton = styled(Button)`
  margin-top: ${Spacing[2]};
`;

const FileUploadLabelContainer = styled('div')`
  display: flex;
  flex-direction: column;
  margin-top: ${Spacing[6]};
`;

const MoreContainer = styled.div.attrs(() => ({ role: 'button' }))`
  position: absolute;
  right: 0;
  top: 0;
  margin-top: 27px;
  margin-right: 16px;

  display: flex;
  align-items: center;
  cursor: pointer;
  padding: 0 ${Spacing[2]};

  & path {
    stroke: ${({ theme }) => theme.color.text.tertiary};
  }

  &:hover path {
    stroke: ${({ theme }) => theme.color.text.disabled};
  }
`;

const RelativeDiv = styled('div')`
  position: relative;
`;

const StyledFileUpload = styled(FileUpload)`
  height: 240px;
`;

interface AttachmentsGalleryEntryProps {
  attachment: CaseAttachment;
  showDocumentPreview: boolean;
  handleDeleteCaseAttachment: (attachmentKey: string) => void;
  showMessage: (message: string, messageType: MessageType) => void;
}

const AttachmentsGalleryEntry = ({
  attachment,
  showDocumentPreview,
  handleDeleteCaseAttachment,
  showMessage,
}: AttachmentsGalleryEntryProps) => {
  const [
    showDeleteAttachmentModal,
    {
      prompt: deleteAttachmentPrompt,
      confirm: confirmDeleteAttachment,
      cancel: cancelDeleteAttachment,
    },
  ] = useConfirmation();

  const handleClickDeleteAttachment = () =>
    deleteAttachmentPrompt(() => handleDeleteCaseAttachment(attachment.key));

  const moreItems = [
    {
      id: `case-attachment__download-attachment-${attachment.key}`,
      name: `case-attachment__download-attachment-${attachment.key}`,
      'data-test': `case-attachment__download-attachment-${attachment.key}`,
      onClick: async () => {
        try {
          await download({ url: attachment.url, filename: attachment.filename });
        } catch {
          showMessage('Failed to download file', MessageType.Failure);
        }
      },
      label: 'Download',
    },
    {
      id: `case-attachment__delete-attachment-${attachment.key}`,
      name: `case-attachment__delete-attachment-${attachment.key}`,
      'data-test': `case-attachment__delete-attachment-${attachment.key}`,
      onClick: handleClickDeleteAttachment,
      label: 'Delete',
      variant: 'danger' as const,
    },
  ];

  return (
    <>
      <ModalWrapper isOpen={showDeleteAttachmentModal} handleCloseModal={cancelDeleteAttachment}>
        <ConfirmationModal
          cancelAction={cancelDeleteAttachment}
          confirmAction={confirmDeleteAttachment}
          header='Delete attachment?'
          confirmActionText='Delete'
        >
          {`${attachment.filename} will be deleted. This action is permanent and cannot be undone.`}
        </ConfirmationModal>
      </ModalWrapper>
      <RelativeDiv>
        <MoreContainer>
          <DropdownMenu
            trigger={
              <IconButton id={`case-attachment__more-${attachment.key}`}>
                <MoreIcon />
              </IconButton>
            }
            align='end'
          >
            {moreItems.map((item) => (
              <DropdownMenu.Item
                data-test={item['data-test']}
                id={item.id}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  item.onClick();
                }}
                key={item.id}
                variant={item.variant}
              >
                {item.label}
              </DropdownMenu.Item>
            ))}
          </DropdownMenu>
        </MoreContainer>
        <AttachmentContainer data-test={`case-attachment_${attachment.filename}`}>
          {showDocumentPreview && (
            <AsyncImage
              value={[
                {
                  url: attachment.url,
                  label: attachment.filename,
                  name: attachment.filename,
                  type: attachment['file-type'],
                },
              ]}
              width='120px'
              height='160px'
              showExtension
            />
          )}
          <AttributeGrid colCount={2}>
            <AttributeItem title='File Name' wordBreak='break-word' value={attachment.filename} />
            <AttributeItem title='File Size' value={formatNumberLarge(attachment['byte-size'])} />
          </AttributeGrid>
        </AttachmentContainer>
      </RelativeDiv>
    </>
  );
};

interface AttachmentsGalleryProps {
  attachments: CaseAttachment[];
  showDocumentPreview: boolean;
  handleDeleteCaseAttachment: (attachmentKey: string) => void;
  showMessage: (message: string, messageType: MessageType) => void;
}

export const AttachmentsGallery = ({
  attachments,
  handleDeleteCaseAttachment,
  showMessage,
  showDocumentPreview,
}: AttachmentsGalleryProps) => {
  return (
    <InfoSection>
      {attachments.map((attachment) => (
        <AttachmentsGalleryEntry
          key={`case-attachment-gallery_${attachment.key}`}
          attachment={attachment}
          showDocumentPreview={showDocumentPreview}
          handleDeleteCaseAttachment={handleDeleteCaseAttachment}
          showMessage={showMessage}
        />
      ))}
    </InfoSection>
  );
};
export interface CaseAttachmentsProps {
  case: Case;
  handleAddCaseAttachment: (attachments: PreparedFileUpload[]) => void;
  handleDeleteCaseAttachment: (attachmentKey: string) => void;
  showMessage: (message: string, messageType: MessageType) => void;
  isPreview?: boolean;
  isPrint?: boolean;
}

const CaseAttachments = ({
  case: kase,
  handleAddCaseAttachment,
  handleDeleteCaseAttachment,
  showMessage,
  isPreview = false,
  isPrint = false,
}: CaseAttachmentsProps) => {
  const handleUpload = async (file: FileWithPreview) => {
    try {
      const result = await readFile(file);
      const attachment = createAttachment({
        filename: file.name,
        fileType: file.type,
        dataUrl: result as string,
      });
      handleAddCaseAttachment([attachment]);
    } catch (error) {
      showMessage('Failed to upload case attachment', MessageType.Failure);
      captureMessage('Failed to upload case attachment.', 'error', {
        error,
        object: kase.id,
        component: 'CaseAttachments',
      });
    }
  };

  return (
    <>
      <Card>
        <Card.Body>
          <Heading size='sm'>
            {kase.attributes['case-attachments'].length > 0
              ? `Attachments (${kase.attributes['case-attachments'].length})`
              : 'Attachments'}
          </Heading>
          <Spacer height={1} />
          {!isPrint && (
            <>
              <fieldset disabled={isPreview}>
                <StyledFileUpload
                  onChange={(file) => {
                    if (file) {
                      handleUpload(file);
                    }
                  }}
                  accept={ACCEPTED_FILE_FORMATS}
                  maxSize={MAX_ATTACHMENT_SIZE_BYTES}
                  maxFiles={MAX_FILE_COUNT}
                  label={
                    <FileUploadLabelContainer>
                      <IconUploadCentered />
                      <Paragraph>Drag and drop up to 10 files (max 25MB)</Paragraph>
                      <FileUploadButton type='button'>Select file</FileUploadButton>
                    </FileUploadLabelContainer>
                  }
                  onReject={(fileRejections) => {
                    const errorMessage = fileRejections[0]?.errors[0]?.message;

                    showMessage(
                      `Failed to upload. ${errorMessage ?? 'Unknown error'}`,
                      MessageType.Failure,
                    );
                  }}
                />
              </fieldset>
              <Spacer height={1} />
            </>
          )}
          <AttachmentsGallery
            attachments={kase.attributes['case-attachments']}
            showDocumentPreview={!isPrint}
            handleDeleteCaseAttachment={handleDeleteCaseAttachment}
            showMessage={showMessage}
          />
        </Card.Body>
      </Card>
    </>
  );
};

export default CaseAttachments;
